import { Typography } from '@euler/app/components/typography';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { SecondaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiAnswerAction } from '@euler/model/bi/answer';
import { BiUploadView } from '@euler/model/bi/upload';
import dayjs from 'dayjs';
import { FilePlus2 } from 'lucide-react-native';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { DatasetChoiceRow } from '../bi-dataset-choice/DatasetChoiceRow';
import { useBiTap } from '../bi-visualization/functions/useBiTap';

/**
 * The same file was imported before: **Use existing** picks one of the
 * datasets made from it (the conversation goes on with it), **Import as
 * new** goes on to the sheets — straight to the import when the file has
 * only one sheet to import.
 */
export const DuplicateOffer = memo(
  ({
    view,
    onImportAsNew,
  }: {
    view: BiUploadView;
    onImportAsNew: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { pending, busy, tap } = useBiTap();
    const [chosen, setChosen] = useState<string | null>(null);
    const open = view.sheets.filter(sheet => !sheet.block);
    const disabled = busy || !!pending || !!chosen;
    const importAsNew = () => {
      if (open.length !== 1) {
        onImportAsNew();
        return;
      }
      // A refused tap leaves the offer to answer again.
      const [only] = open;
      tap({
        action: BiAnswerAction.SelectSheets,
        uploadId: view.id,
        sheets: [only.name],
      });
    };
    if (chosen) return <CardNotice text={`已选择「${chosen}」`} />;
    return (
      <View style={{ gap: rpx(10) }}>
        <Typography.Text
          size={rpx(13)}
          lineHeight={rpx(20)}
          color={aiColors.foreground}
        >
          这个文件之前导入过，可以直接用已有的数据：
        </Typography.Text>
        <View style={{ gap: rpx(8) }}>
          {view.duplicates.map(duplicate => (
            <DatasetChoiceRow
              key={duplicate.datasetId}
              name={duplicate.name}
              facts={[
                duplicate.sheet ? `工作表「${duplicate.sheet}」` : null,
                `${dayjs(duplicate.createdAt).format('M月D日')}导入`,
              ]}
              disabled={disabled}
              onPress={() => {
                if (
                  tap(
                    {
                      action: BiAnswerAction.UseExistingDataset,
                      uploadId: view.id,
                      datasetId: duplicate.datasetId,
                    },
                    duplicate.name,
                  )
                )
                  setChosen(duplicate.name);
              }}
            />
          ))}
        </View>
        <SecondaryButton
          title="作为新数据导入"
          icon={FilePlus2}
          disabled={disabled}
          onPress={importAsNew}
        />
      </View>
    );
  },
);
